import { useEffect, useState } from 'react'
import { Modal } from '@/components/common'
import { extractErrorMessage } from '@/utils/errorHandler'

interface FinanceDecisionDialogProps {
  isOpen: boolean
  kind: 'requester_deduction' | 'substitute_payout'
  requestId: string
  currentValue?: string | null
  personalValue?: string | null
  schoolDefault?: string | null
  onClose: () => void
  onSuccess: (value?: string) => void
}

/**
 * V3/B7b: Finanz-Entscheidung der Lehrer (Direktor-Konzept Kap. 5.3/E5).
 * (a) Anfragender Lehrer (privat): IPB vs. Lohn
 * (b) Vertretungs-Lehrer: ausbezahlt vs. Lektionenkonto
 */
export function FinanceDecisionDialog({
  isOpen, kind, requestId, currentValue, personalValue, schoolDefault, onClose, onSuccess,
}: FinanceDecisionDialogProps) {
  // IPB-v3 (I3): persönliche Wahl ?? HR/Anzeige-Wert ?? Schul-Default vorauswählen
  const initial = personalValue ?? currentValue ?? schoolDefault ?? ''
  const [value, setValue] = useState<string>(initial)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setValue(personalValue ?? currentValue ?? schoolDefault ?? '')
    setNote('')
    setError(null)
  }, [isOpen, currentValue, personalValue, schoolDefault, requestId])

  const isA = kind === 'requester_deduction'

  const handleSubmit = async () => {
    if (!value) {
      setError('Bitte wählen Sie eine Option.')
      return
    }
    const personal = personalValue ?? currentValue ?? null
    if (value === personal || (personal == null && schoolDefault && value === schoolDefault)) {
      onClose()
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      if (isA) {
        const { teacherFinanceApi } = await import('@/services/api/teacher')
        await teacherFinanceApi.setRequesterDeduction(
          requestId, value as 'ipb' | 'salary', note || undefined)
      } else {
        const { teacherApi } = await import('@/services/api/teacher')
        await teacherApi.setSubstitutePayout(
          requestId, value as 'paid_out' | 'lesson_account')
      }
      onSuccess(value)
      onClose()
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Entscheidung fehlgeschlagen' }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}
      title={isA ? 'Finanz-Wahl: IPB oder Lohnabzug' : 'Finanz-Wahl: IPB oder Auszahlung'} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {isA
            ? 'Wird der Betrag (privat) vom Stundenkonto (IPB) abgezogen oder als Lohnabzug verrechnet?'
            : 'Wird die übernommene Lektion ausbezahlt oder geht sie aufs Lektionenkonto (IPB)?'}
        </p>
        {/* HR-v2 (D8): Frist-Hinweis — die Wahl ist ab Lektionsbeginn bindend;
            das Backend lehnt Änderungen danach mit 422 ab. */}
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
          Die Wahl ist <strong>bis zum Beginn der ersten Lektion</strong> änderbar —
          danach ist sie bindend und HR rechnet nach den Schul-Defaults ab.
        </p>
        <div className="space-y-2">
          {(isA
            ? [{ v: 'ipb', l: 'IPB' }, { v: 'salary', l: 'Lohnabzug' }]
            : [{ v: 'lesson_account', l: 'Lektionenkonto (IPB)' }, { v: 'paid_out', l: 'Auszahlung' }]
          ).map(opt => (
            <label key={opt.v} className="flex items-center gap-2 p-2 border border-gray-200 rounded-md cursor-pointer">
              <input type="radio" name="finance-value" checked={value === opt.v}
                onChange={() => setValue(opt.v)} className="w-4 h-4" />
              <span className="text-sm">{opt.l}</span>
            </label>
          ))}
        </div>
        {personalValue == null && schoolDefault && (
          <p className="text-xs text-gray-400">Schul-Standard ist vorausgewählt.</p>
        )}
        {isA && (
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
            placeholder="Optionale Notiz..." />
        )}
        {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Abbrechen</button>
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
            {submitting ? 'Speichern...' : 'Entscheidung speichern'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
